/**
 * Texturen voor het boorprofiel: welke kleur, welk patroon en welke markeringkleur een
 * grondsoort, bijzonder bestanddeel (BZB), vulmateriaal van het boorgat of onderdeel van de
 * peilbuis krijgt.
 *
 * De standaard volgt NEN 6693, Figuur 1 (hoofdnaam/symbool/markering). Een bedrijf mag per
 * codelijst-waarde afwijken; alleen die afwijkingen worden opgeslagen. Eén bron voor de
 * backoffice (SVG-tekening + PDF) en de veldapp (react-native-svg): de patronen staan hier als
 * geometrie, elke tekening zet ze om naar zijn eigen SVG-vorm.
 *
 * Voor BZB's kent de norm geen symbolen. Standaard is een BZB daarom wit en zonder patroon
 * (BZB_DEFAULT): als bijmenging wordt hij dan niet getekend (isBlankTexture), en een laag die
 * uit een BZB bestaat wordt een witte laag. Pas als een bedrijf een kleur of patroon kiest,
 * verschijnt de BZB als balk in het profiel. De families (puin, asfalt, ...) bepalen de
 * groepering op de instellingenpagina.
 *
 * Eén uitzondering: asfalt is standaard donkergrijs (BZB_ASPHALT). Een asfaltlaag is in een
 * profiel een eigen laag bovenaan; wit zag hij eruit als een gat in de tekening.
 *
 * Afwerking (vulmateriaal) en peilbuis zijn geen grondclassificatie en gelden voor beide normen:
 * die regels hebben geen standaard (`standard: null`, sleutel `categorie:code`). Hun standaard is
 * wat de tekening altijd al deed, zodat er zonder afwijkingen niets verandert.
 */

import { BODEMSOORT, BODEMSOORT_HISTORISCH, BZB_TYPE } from './codelists/index.js';
import * as nen6693 from './codelists/nen6693.js';
import type { SoilStandard } from './standards.js';

// ─── Patronen ───────────────────────────────────────────────────────────────

export type TexturePattern =
  | 'none'
  | 'dots-fine'
  | 'dots-coarse'
  | 'lines-vertical'
  | 'lines-horizontal'
  | 'lines-diagonal'
  | 'cross-diagonal'
  | 'waves'
  | 'dots-large'
  | 'lines-horizontal-fine'
  // Extra keuzes voor de bedrijfstexturen (geïnspireerd op de arceringen van TerraIndex).
  | 'dots-dense' | 'dots-sparse' | 'circles-open' | 'squares-open' | 'crosses' | 'dashes'
  | 'dashes-vertical' | 'dash-dot' | 'lines-vertical-wide' | 'lines-horizontal-wide' | 'lines-diagonal-left'
  | 'lines-diagonal-dense' | 'grid' | 'bricks' | 'triangles' | 'triangles-open' | 'diamonds' | 'diamonds-filled'
  | 'hexagons' | 'chevrons' | 'arcs' | 'scales';

/**
 * Eén vorm binnen een patroontegel. Lijnen en paden krijgen de markeringkleur als stroke; een pad
 * met `filled` wordt ook gevuld (dichte driehoeken, ruiten).
 */
export type PatternShape =
  | { type: 'circle'; cx: number; cy: number; r: number }
  | { type: 'line'; x1: number; y1: number; x2: number; y2: number; strokeWidth: number }
  | { type: 'path'; d: string; strokeWidth: number; filled?: boolean };

export interface PatternDef {
  id: TexturePattern;
  label: string;
  width: number;
  height: number;
  /** Rotatie van de tegel in graden (SVG patternTransform). */
  rotate?: number;
  shapes: readonly PatternShape[];
}

// Maten en vormen zijn overgenomen uit de bestaande boorprofiel-tekening, zodat een profiel
// zonder afwijkingen er precies hetzelfde uitziet als voorheen.
export const TEXTURE_PATTERNS: readonly PatternDef[] = [
  { id: 'dots-fine', label: 'Fijn gestippeld', width: 6, height: 6, shapes: [
    { type: 'circle', cx: 1.5, cy: 1.5, r: 0.55 },
    { type: 'circle', cx: 4.5, cy: 4.5, r: 0.55 },
  ] },
  { id: 'dots-coarse', label: 'Grof gestippeld', width: 9, height: 9, shapes: [
    { type: 'circle', cx: 2.5, cy: 2.5, r: 1.15 },
    { type: 'circle', cx: 7, cy: 7, r: 1.15 },
  ] },
  { id: 'lines-vertical', label: 'Verticaal gestreept', width: 5, height: 5, shapes: [
    { type: 'line', x1: 1.5, y1: 0, x2: 1.5, y2: 5, strokeWidth: 0.9 },
  ] },
  { id: 'lines-horizontal', label: 'Horizontaal gestreept', width: 6, height: 5, shapes: [
    { type: 'line', x1: 0, y1: 1.5, x2: 6, y2: 1.5, strokeWidth: 0.9 },
  ] },
  { id: 'lines-diagonal', label: 'Diagonaal', width: 6, height: 6, rotate: 45, shapes: [
    { type: 'line', x1: 0, y1: 0, x2: 0, y2: 6, strokeWidth: 0.9 },
  ] },
  { id: 'cross-diagonal', label: 'Dubbel diagonaal', width: 8, height: 8, shapes: [
    { type: 'path', d: 'M-2 2 L2 -2 M0 8 L8 0 M6 10 L10 6', strokeWidth: 0.9 },
    { type: 'path', d: 'M-2 6 L2 10 M0 0 L8 8 M6 -2 L10 2', strokeWidth: 0.9 },
  ] },
  { id: 'waves', label: 'Golven', width: 10, height: 5, shapes: [
    { type: 'path', d: 'M0 2.5 q 2.5 -2 5 0 t 5 0', strokeWidth: 0.8 },
  ] },
  // Uit de boorgatkolom: filtergrind en het filter van de peilbuis.
  { id: 'dots-large', label: 'Grove korrels', width: 8, height: 8, shapes: [
    { type: 'circle', cx: 2.5, cy: 2.5, r: 1.5 },
    { type: 'circle', cx: 6, cy: 6.5, r: 1.3 },
  ] },
  { id: 'lines-horizontal-fine', label: 'Fijn horizontaal gestreept', width: 5, height: 4, shapes: [
    { type: 'line', x1: 0, y1: 1.5, x2: 5, y2: 1.5, strokeWidth: 0.9 },
  ] },
  // ── Extra keuzes. Alleen cirkels, lijnen en paden, zodat backoffice, PDF en veldapp ze tekenen.
  { id: 'dots-dense', label: 'Dicht gestippeld', width: 4, height: 4, shapes: [
    { type: 'circle', cx: 1, cy: 1, r: 0.5 }, { type: 'circle', cx: 3, cy: 3, r: 0.5 },
  ] },
  { id: 'dots-sparse', label: 'Verspreide stippen', width: 12, height: 12, shapes: [
    { type: 'circle', cx: 3, cy: 3, r: 0.7 }, { type: 'circle', cx: 9, cy: 9, r: 0.7 },
  ] },
  { id: 'circles-open', label: 'Open rondjes', width: 10, height: 10, shapes: [
    { type: 'path', d: 'M4.3 2.5 a1.8 1.8 0 1 0 0.01 0 M9.3 7.5 a1.8 1.8 0 1 0 0.01 0', strokeWidth: 0.6 },
  ] },
  { id: 'squares-open', label: 'Blokjes', width: 10, height: 10, shapes: [
    { type: 'path', d: 'M1.5 1.5 h3 v3 h-3 Z M6.5 6.5 h3 v3 h-3 Z', strokeWidth: 0.6 },
  ] },
  { id: 'crosses', label: 'Kruisjes', width: 10, height: 10, shapes: [
    { type: 'path', d: 'M3 1.5 V4.5 M1.5 3 H4.5 M8 6.5 V9.5 M6.5 8 H9.5', strokeWidth: 0.6 },
  ] },
  { id: 'dashes', label: 'Streepjes', width: 10, height: 8, shapes: [
    { type: 'path', d: 'M1 2 H5 M6 6 H10', strokeWidth: 0.8 },
  ] },
  { id: 'dashes-vertical', label: 'Verticale streepjes', width: 8, height: 10, shapes: [
    { type: 'path', d: 'M2 1 V5 M6 6 V10', strokeWidth: 0.8 },
  ] },
  { id: 'dash-dot', label: 'Streep-stip', width: 12, height: 6, shapes: [
    { type: 'line', x1: 1, y1: 3, x2: 7, y2: 3, strokeWidth: 0.8 },
    { type: 'circle', cx: 9.5, cy: 3, r: 0.6 },
  ] },
  { id: 'lines-vertical-wide', label: 'Verticaal breed', width: 9, height: 9, shapes: [
    { type: 'line', x1: 1, y1: 0, x2: 1, y2: 9, strokeWidth: 0.8 },
  ] },
  { id: 'lines-horizontal-wide', label: 'Horizontaal breed', width: 9, height: 9, shapes: [
    { type: 'line', x1: 0, y1: 1, x2: 9, y2: 1, strokeWidth: 0.8 },
  ] },
  { id: 'lines-diagonal-left', label: 'Diagonaal andersom', width: 6, height: 6, rotate: -45, shapes: [
    { type: 'line', x1: 0, y1: 0, x2: 0, y2: 6, strokeWidth: 0.9 },
  ] },
  { id: 'lines-diagonal-dense', label: 'Diagonaal dicht', width: 3.5, height: 3.5, rotate: 45, shapes: [
    { type: 'line', x1: 0, y1: 0, x2: 0, y2: 3.5, strokeWidth: 0.6 },
  ] },
  { id: 'grid', label: 'Ruitjes', width: 6, height: 6, shapes: [
    { type: 'path', d: 'M0 0.3 H6 M0.3 0 V6', strokeWidth: 0.6 },
  ] },
  { id: 'bricks', label: 'Baksteen', width: 12, height: 8, shapes: [
    { type: 'path', d: 'M0 0.3 H12 M0 4.3 H12 M0.3 0.3 V4.3 M6.3 4.3 V8', strokeWidth: 0.6 },
  ] },
  { id: 'triangles', label: 'Driehoeken', width: 10, height: 9, shapes: [
    { type: 'path', d: 'M1 7.5 L5 1.5 L9 7.5 Z', strokeWidth: 0.4, filled: true },
  ] },
  { id: 'triangles-open', label: 'Open driehoeken', width: 10, height: 9, shapes: [
    { type: 'path', d: 'M1.5 7.5 L5 2 L8.5 7.5 Z', strokeWidth: 0.6 },
  ] },
  { id: 'diamonds', label: 'Ruiten', width: 10, height: 10, shapes: [
    { type: 'path', d: 'M5 1.5 L8.5 5 L5 8.5 L1.5 5 Z', strokeWidth: 0.6 },
  ] },
  { id: 'diamonds-filled', label: 'Dichte ruiten', width: 10, height: 10, shapes: [
    { type: 'path', d: 'M5 2 L8 5 L5 8 L2 5 Z', strokeWidth: 0.4, filled: true },
  ] },
  { id: 'hexagons', label: 'Honingraat', width: 15, height: 8.66, shapes: [
    { type: 'path', d: 'M0 4.33 L2.5 0 L7.5 0 L10 4.33 L7.5 8.66 L2.5 8.66 Z M10 4.33 L15 4.33', strokeWidth: 0.6 },
  ] },
  { id: 'chevrons', label: 'Hoekjes', width: 10, height: 8, shapes: [
    { type: 'path', d: 'M1.5 2.5 L5 5.5 L8.5 2.5', strokeWidth: 0.7 },
  ] },
  { id: 'arcs', label: 'Boogjes', width: 10, height: 8, shapes: [
    { type: 'path', d: 'M1.5 2.5 Q5 7.5 8.5 2.5', strokeWidth: 0.7 },
  ] },
  { id: 'scales', label: 'Schubben', width: 8, height: 6, shapes: [
    { type: 'path', d: 'M0 6 Q4 0 8 6', strokeWidth: 0.6 },
  ] },
  { id: 'none', label: 'Leeg', width: 6, height: 6, shapes: [] },
];

const PATTERN_IDS: ReadonlySet<string> = new Set(TEXTURE_PATTERNS.map((p) => p.id));

export function getPatternDef(id: TexturePattern): PatternDef {
  return TEXTURE_PATTERNS.find((p) => p.id === id) ?? TEXTURE_PATTERNS.find((p) => p.id === 'none')!;
}

// ─── Textuur ────────────────────────────────────────────────────────────────

export interface Texture {
  /** Achtergrondkleur, #rrggbb. */
  fill: string;
  pattern: TexturePattern;
  /** Kleur van de stippen/strepen, #rrggbb. */
  mark: string;
  /**
   * Alleen bij een BZB: waarschuwen. Komt de BZB in een laag voor, dan krijgt die laag een
   * waarschuwingsdriehoek in het boorprofiel (ook als de BZB zelf niet getekend wordt).
   */
  warn?: boolean;
}

/** De acht symbolen van NEN 6693, Figuur 1. */
export type SoilFamily = 'zand' | 'grind' | 'silt' | 'klei' | 'veen' | 'slib' | 'water' | 'bijzonder';

// Gemeten uit de tabel in Figuur 1. De markering is overal zwart, behalve bij slib (wit op zwart).
export const NEN_FIGURE_1: Readonly<Record<SoilFamily, Texture>> = {
  zand:      { fill: '#fdd963', pattern: 'dots-fine',        mark: '#000000' },
  grind:     { fill: '#f8823e', pattern: 'dots-coarse',      mark: '#000000' },
  silt:      { fill: '#a6a6a6', pattern: 'lines-vertical',   mark: '#000000' },
  klei:      { fill: '#a9d18d', pattern: 'lines-diagonal',   mark: '#000000' },
  veen:      { fill: '#833c0a', pattern: 'lines-horizontal', mark: '#000000' },
  slib:      { fill: '#000000', pattern: 'cross-diagonal',   mark: '#ffffff' },
  water:     { fill: '#0066ff', pattern: 'waves',            mark: '#000000' },
  bijzonder: { fill: '#ffffff', pattern: 'none',             mark: '#000000' },
};

/** De standaard van elke BZB: wit, geen patroon. Zie isBlankTexture. */
export const BZB_DEFAULT: Texture = { fill: '#ffffff', pattern: 'none', mark: '#000000' };
/** Asfalt, teer en bitumen: donkergrijs, zoals een asfaltlaag op elk boorprofiel staat. */
export const BZB_ASPHALT: Texture = { fill: '#3d3d3d', pattern: 'none', mark: '#000000' };

/** De standaardtextuur van een BZB naar zijn familie: asfalt donkergrijs, de rest wit. */
export const bzbDefaultTexture = (family: string | null | undefined): Texture =>
  (family === 'asfalt' ? BZB_ASPHALT : BZB_DEFAULT);

/**
 * Wit en zonder patroon: niets te tekenen. Een BZB-balk met deze textuur laten we weg; zou je
 * hem tekenen, dan staat er een wit streepje in de laag dat niets zegt.
 */
export function isBlankTexture(t: Texture | null | undefined): boolean {
  return !t || (t.pattern === 'none' && t.fill.toLowerCase() === '#ffffff');
}

/** BZB-families zonder grond-equivalent; alleen voor de groepering op de instellingenpagina. */
export type BzbFamily = 'organisch' | 'puin' | 'asfalt' | 'slakken' | 'afval' | 'gesteente' | 'mineraal' | 'lucht' | 'overig';

/** Groepen in de tabs Afwerking en Peilbuis. */
export type InstallFamily = 'afdichting' | 'filtermateriaal' | 'aanvulling' | 'achtergelaten' | 'bovenafwerking' | 'peilbuis' | 'meting';

/** Groepnaam voor de instellingenpagina. */
export const TEXTURE_GROUP_LABELS: Readonly<Record<SoilFamily | BzbFamily | InstallFamily, string>> = {
  afdichting: 'Materiaal', filtermateriaal: 'Filter', aanvulling: 'Aanvulling',
  achtergelaten: 'Achtergelaten in de grond', bovenafwerking: 'Bovenafwerking', peilbuis: 'Peilbuis', meting: 'Metingen',
  zand: 'Zand', grind: 'Grind', silt: 'Silt en leem', klei: 'Klei', veen: 'Veen en organisch',
  slib: 'Slib', water: 'Water', bijzonder: 'Gesteente en bijzondere lagen',
  organisch: 'Plantaardig en hout', puin: 'Puin en bouwmaterialen', asfalt: 'Asfalt en teer',
  slakken: 'Slakken, as en kolen', afval: 'Afval en kunststof', gesteente: 'Stenen en gesteente',
  mineraal: 'Mineralen', lucht: 'Lucht', overig: 'Overig',
};

// ─── Indeling van codes in families ─────────────────────────────────────────

const ORGANIC_SOIL = ['veen', 'humus', 'humeus', 'detritus', 'gyttja', 'bruinkool', 'turf', 'teelaarde', 'organische stof', 'dy'];

/** Grondsoort-label → Figuur 1-symbool. Zelfde regels als de tekening tot nu toe. */
function soilFamilyOfLabel(label: string): SoilFamily {
  const s = label.toLowerCase();
  if (s.includes('grind') || s.includes('keien')) return 'grind';
  if (s.includes('zand')) return 'zand';
  if (s.includes('klei')) return 'klei';
  if (s.includes('silt') || s.includes('leem')) return 'silt';
  if (ORGANIC_SOIL.some((o) => s.includes(o))) return 'veen';
  if (s.includes('slib')) return 'slib';
  if (s.includes('water')) return 'water';
  return 'bijzonder';
}

// Volgorde telt: de eerste familie met een treffer wint. Specifiek vóór algemeen, dus
// 'asfaltgranulaat' is asfalt (niet puin), 'mijnsteen' slakken en 'kalksteen' gesteente (niet
// kalk). Een naald met '=' ervoor telt alleen als heel woord: 'stab' mag 'gestabiliseerd' niet
// raken en 'as' niet 'asbest'.
const BZB_RULES: ReadonlyArray<[BzbFamily | SoilFamily, readonly string[]]> = [
  ['lucht', ['lucht']],
  ['water', ['water']],
  // Heten 'afval' maar zijn het niet in de zin van de afval-familie.
  ['organisch', ['bladafval']],
  ['puin', ['bouw- en sloop']],
  ['asfalt', ['asfalt', 'bitumen', '=teer', '=dab', '=oab', '=gab', '=sma', '=zoab', '=stab', '=eab', '=wab', '=ac', 'novachip',
    'deklaag', 'slijtlaag', 'slemlaag', 'kleeflaag', 'penetratie', 'microville', 'stabiflex', '=stol', '=repac', '=brac',
    'oppervlakbehandeling', '?']],
  ['slakken', ['slak', 'sintel', '=as', 'assen', 'vliegas', 'bodemas', 'koolas', 'zinkas', 'kolen', 'kool', 'verbrand',
    'mijnsteen', 'ligniet', '=bims', 'lavalith']],
  ['afval', ['afval', 'plastic', 'kunststof', 'piepschuim', '=eps', 'folie', 'rubber', 'banden', 'textiel', 'metaal', 'glas',
    '=verf', '=olie', 'munitie', 'huisvuil', '=leer', 'worteldoek', 'asbest', 'artefact']],
  ['puin', ['puin', 'baksteen', 'beton', 'klinker', 'kassei', 'straatsten', 'tegel', 'stelcon', 'granulaat', 'keramiek',
    'aardewerk', 'dakpan', 'gresbuis', 'draineerbuis', 'cement', 'verharding', 'ophoog', 'vullaag', '=bouw', 'bewerkt steen',
    'ballast', '=split', 'steenslag', 'bentoniet']],
  ['organisch', ['hout', 'plant', 'wortel', '=riet', 'blad', 'duizendknoop', '=bot', 'coproliet']],
  ['gesteente', ['steen', 'stenen', 'rots', 'blokken', 'basalt', 'krijt', 'mergel', 'schist', 'schalie', 'silex', 'porfier',
    'doleriet', 'conglomeraat', 'breccie', '=lava', 'gesteente', 'kalknodule', 'kalkkorst']],
  ['grind', ['grind', 'gravel', 'kiezel', 'keien']],
  ['zand', ['zand', 'flugsand']],
  ['klei', ['klei', 'keileem']],
  ['silt', ['silt', 'leem', '=loss']],
  ['veen', ORGANIC_SOIL],
  ['slib', ['slib']],
  ['mineraal', ['ijzer', '=oer', 'roest', 'schelp', 'glauconiet', '=oker', 'fosfaat', 'fosfor', 'erts', 'glimmer',
    'mineralen', '=gley', '=kalk', '=gips', 'gipspleister', 'koraal']],
];

const SOIL_FAMILIES: ReadonlySet<string> = new Set(Object.keys(NEN_FIGURE_1));

function bzbFamilyOfLabel(label: string, isRock: boolean): BzbFamily | SoilFamily {
  const s = label.toLowerCase();
  // Een gesteente (NEN 6693 Tabel C.2) is altijd gesteente: 'zandsteen' is geen zand.
  if (isRock) return 'gesteente';
  const words = s.split(/[^a-z0-9?]+/).filter(Boolean);
  const hit = (needle: string) => (needle.startsWith('=') ? words.includes(needle.slice(1)) : s.includes(needle));
  for (const [family, needles] of BZB_RULES) if (needles.some(hit)) return family;
  return 'overig';
}

/**
 * De familie van een bijzonder bestanddeel op naam ('Baksteen' → puin, 'Hout' → organisch).
 * Dezelfde indeling als de texturen, zodat het filter op de labopdracht en de tekening het
 * over hetzelfde bestanddeel eens zijn.
 */
export function classifyBzbLabel(label: string): BzbFamily | SoilFamily {
  return bzbFamilyOfLabel(label, false);
}

function defaultForFamily(family: BzbFamily | SoilFamily): Texture {
  return SOIL_FAMILIES.has(family) ? NEN_FIGURE_1[family as SoilFamily] : BZB_DEFAULT;
}

// ─── Catalogus: alle instelbare codes ──────────────────────────────────────

export type TextureCategory = 'soil' | 'bzb' | 'afwerking' | 'peilbuis';
/** Categorieën die voor beide normen gelden. */
export type InstallCategory = 'afwerking' | 'peilbuis';

/** Onderdelen van de peilbuis en de grondwaterstand (codes in de catalogus, tab Peilbuis). */
export const PEILBUIS_PARTS = { blind: 'BLIND', filter: 'FILTER', gws: 'GWS' } as const;

export interface TextureEntry {
  /**
   * De sleutel waaronder een afwijking wordt opgeslagen: `standard:category:code`, of voor een
   * samengevoegde reeks `standard:category:group:<id>`.
   */
  key: string;
  /** null = geldt voor beide normen (afwerking, peilbuis). */
  standard: SoilStandard | null;
  category: TextureCategory;
  /** Alle codes die deze instelling volgen; één, behalve bij een samengevoegde reeks. */
  codes: string[];
  label: string;
  /** Bij een samengevoegde reeks: de labels van de codes erin (voor zoeken en uitleg). */
  memberLabels: string[];
  family: SoilFamily | BzbFamily | InstallFamily;
  defaultTexture: Texture;
  /** Alleen de kleur telt (een lijn, zoals de grondwaterstand): geen patroon of markering. */
  colorOnly?: boolean;
}

export function textureKey(standard: SoilStandard | null, category: TextureCategory, code: string): string {
  return standard ? `${standard}:${category}:${code}` : `${category}:${code}`;
}

const plain = (fill: string): Texture => ({ fill, pattern: 'none', mark: '#000000' });

/**
 * Boorgat en peilbuis, met als standaard precies wat de tekening tot nu toe deed. Filterzand
 * en aanvulzand tekenden als zand uit Figuur 1, aanvulgrind als grind; de drie soorten
 * bentoniet als één groen. Namen van vulmaterialen komen uit nen6693.VULMATERIAAL.
 */
/** Wat achtergelaten is in de grond (casing, diver, ...) tekende altijd als één oranje balkje. */
const LEFT_BEHIND_DEFAULT: Texture = plain('#e08a4a');

const INSTALL_ENTRIES: ReadonlyArray<{ category: InstallCategory; code: string; label?: string; family: InstallFamily; t: Texture; colorOnly?: boolean }> = [
  { category: 'afwerking', code: 'VUL_BK', family: 'afdichting', t: plain('#bfe0b2') },
  { category: 'afwerking', code: 'VUL_BP', family: 'afdichting', t: plain('#bfe0b2') },
  { category: 'afwerking', code: 'VUL_BS', family: 'afdichting', t: plain('#bfe0b2') },
  { category: 'afwerking', code: 'VUL_KL', family: 'afdichting', t: plain('#cfe6c4') },
  { category: 'afwerking', code: 'VUL_CE', family: 'afdichting', t: plain('#9aa0a6') },
  { category: 'afwerking', code: 'VUL_FZ', family: 'filtermateriaal', t: NEN_FIGURE_1.zand },
  { category: 'afwerking', code: 'VUL_FG', family: 'filtermateriaal', t: { fill: '#eaf0f3', pattern: 'dots-large', mark: '#7f8c9b' } },
  { category: 'afwerking', code: 'VUL_AZ', family: 'aanvulling', t: NEN_FIGURE_1.zand },
  { category: 'afwerking', code: 'VUL_AG', family: 'aanvulling', t: NEN_FIGURE_1.grind },
  { category: 'afwerking', code: 'VUL_UG', family: 'aanvulling', t: plain('#e7e5df') },
  { category: 'afwerking', code: 'VUL_OP', family: 'aanvulling', t: plain('#ffffff') },
  // Achtergelaten in de grond: één regel per type (nen6693.ACHTERGELATEN_TYPE).
  ...nen6693.ACHTERGELATEN_TYPE.map((a) => ({ category: 'afwerking' as const, code: a.code, label: a.label, family: 'achtergelaten' as const, t: LEFT_BEHIND_DEFAULT })),
  // Bovenafwerking (straatpot, putdeksel, ...): standaard leeg, en dan niet getekend. Met een
  // eigen kleur of patroon een band bovenin het boorgat (TOP_COMPLETION_DEPTH_CM).
  ...nen6693.BOVENAFWERKING.map((b) => ({ category: 'afwerking' as const, code: b.code, label: b.label, family: 'bovenafwerking' as const, t: BZB_DEFAULT })),
  { category: 'peilbuis', code: PEILBUIS_PARTS.blind, label: 'Peilbuis, blind deel', family: 'peilbuis', t: plain('#b9bcc2') },
  { category: 'peilbuis', code: PEILBUIS_PARTS.filter, label: 'Peilbuis, filter', family: 'peilbuis', t: { fill: '#b9bcc2', pattern: 'lines-horizontal-fine', mark: '#4f5966' } },
  { category: 'peilbuis', code: PEILBUIS_PARTS.gws, label: 'Grondwaterstand (lijn)', family: 'meting', t: plain('#2f7fd1'), colorOnly: true },
];

/** Wat de tekening doet met een vulmateriaal dat (nog) niet in de lijst staat. */
const UNKNOWN_FILLING: Texture = plain('#e7e5df');

/**
 * Reeksen die alleen in variant verschillen krijgen samen één instelling. Alle asfaltmengsels
 * (DAB, OAB, SMA, ZOAB, AC, ...) zijn in de tekening gewoon asfalt; honderd losse rijen om
 * hetzelfde in te stellen helpen niemand.
 */
const ROCK_CODES_6693: ReadonlySet<string> = new Set(nen6693.GESTEENTE.values());

const COLLAPSED_GROUPS: ReadonlyArray<{
  id: string;
  category: TextureCategory;
  label: string;
  matches: (label: string, family: SoilFamily | BzbFamily, code: string) => boolean;
}> = [
  // Figuur 1 kent geen symbool voor gesteente: vijftig keer 'leeg' instellen heeft geen zin.
  { id: 'gesteente', category: 'soil', label: 'Rots en gesteente (alle typen)', matches: (_l, _f, code) => code === 'ROTS' || ROCK_CODES_6693.has(code) },
  { id: 'asfalt', category: 'bzb', label: 'Asfalt, teer en bitumen (alle typen)', matches: (_l, family) => family === 'asfalt' },
  { id: 'gesteente', category: 'bzb', label: 'Stenen en gesteente (alle typen)', matches: (_l, family) => family === 'gesteente' },
  { id: 'ballast', category: 'bzb', label: 'Ballast (alle fracties)', matches: (l) => l.toLowerCase().startsWith('ballast') },
  { id: 'verbrandingsresten', category: 'bzb', label: 'Verbrandingsresten (fijn, middelgrof, grof)', matches: (l) => l.toLowerCase().includes('verbrandingsresten') },
];

function buildCatalog(): TextureEntry[] {
  const out: TextureEntry[] = [];
  const byKey = new Map<string, TextureEntry>();
  const add = (standard: SoilStandard, category: TextureCategory, code: string, label: string, family: SoilFamily | BzbFamily) => {
    const group = COLLAPSED_GROUPS.find((g) => g.category === category && g.matches(label, family, code));
    const key = group ? textureKey(standard, category, `group:${group.id}`) : textureKey(standard, category, code);
    const existing = byKey.get(key);
    if (existing) {
      if (group && !existing.codes.includes(code)) { existing.codes.push(code); existing.memberLabels.push(label); }
      return;
    }
    const e: TextureEntry = {
      key, standard, category, codes: [code], label: group ? group.label : label, memberLabels: group ? [label] : [],
      family, defaultTexture: category === 'bzb' ? bzbDefaultTexture(family) : defaultForFamily(family),
    };
    byKey.set(key, e);
    out.push(e);
  };

  // NEN 5104: SIKB-codes. 'S' is hier slib, niet silt.
  for (const [label, code] of BODEMSOORT) add('nen5104', 'soil', code, label, soilFamilyOfLabel(label));
  for (const [label, code] of BZB_TYPE) add('nen5104', 'bzb', code, label, bzbFamilyOfLabel(label, false));

  // NEN 6693: NEN-codes. Gesteente heeft geen symbool in Figuur 1 en blijft leeg.
  for (const [label, code] of nen6693.GRONDSOORT) add('nen6693', 'soil', code, label, soilFamilyOfLabel(label));
  for (const [label, code] of nen6693.LAAGTYPE_SPECIAAL) {
    add('nen6693', 'soil', code, label, code === 'ROTS' || code === 'BVL' || code === 'BIJZ' ? 'bijzonder' : soilFamilyOfLabel(label));
  }
  for (const [label, code] of nen6693.GESTEENTE) add('nen6693', 'soil', code, label, 'bijzonder');
  for (const [label, code] of nen6693.BZB_SOORT) add('nen6693', 'bzb', code, label, bzbFamilyOfLabel(label, false));
  for (const [label, code] of nen6693.GESTEENTE) add('nen6693', 'bzb', code, label, bzbFamilyOfLabel(label, true));

  // Afwerking en peilbuis: één regel voor beide normen.
  for (const i of INSTALL_ENTRIES) {
    out.push({
      key: textureKey(null, i.category, i.code), standard: null, category: i.category, codes: [i.code],
      label: i.label ?? (nen6693.vulmateriaalLabel(i.code) || i.code),
      memberLabels: [], family: i.family, defaultTexture: i.t, ...(i.colorOnly ? { colorOnly: true } : {}),
    });
  }
  return out;
}

/** Alle instelbare texturen, per standaard en categorie. */
export const TEXTURE_CATALOG: readonly TextureEntry[] = buildCatalog();

const CATALOG_BY_KEY: ReadonlyMap<string, TextureEntry> = new Map(TEXTURE_CATALOG.map((e) => [e.key, e]));

/** `standard:category:code` → de catalogusregel die die code volgt (ook binnen een reeks). */
const CATALOG_BY_CODE: ReadonlyMap<string, TextureEntry> = new Map(
  TEXTURE_CATALOG.flatMap((e) => e.codes.map((c) => [textureKey(e.standard, e.category, c), e] as const)),
);

/** Codes die niet meer gekozen kunnen worden maar nog wel in oude lagen staan (5104 'E' = keien). */
const HISTORIC_SOIL_5104: ReadonlyMap<string, string> = new Map(
  [...BODEMSOORT_HISTORISCH].filter(([, code]) => ![...BODEMSOORT.values()].includes(code)).map(([label, code]) => [code, label]),
);

export function getTextureEntry(key: string): TextureEntry | undefined {
  return CATALOG_BY_KEY.get(key);
}

// ─── Afwijkingen van een bedrijf ────────────────────────────────────────────

/** Afwijkingen per catalogussleutel. Ontbreekt een sleutel, dan geldt de standaard. */
export type TextureOverrides = Record<string, Texture>;

const HEX_COLOR = /^#[0-9a-f]{6}$/;

function normalizeColor(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const v = value.trim().toLowerCase();
  return HEX_COLOR.test(v) ? v : null;
}

function sameTexture(a: Texture, b: Texture): boolean {
  return a.fill === b.fill && a.pattern === b.pattern && a.mark === b.mark && !!a.warn === !!b.warn;
}

/**
 * Maak van willekeurige invoer (API-body, opgeslagen JSON) een geldige set afwijkingen.
 *
 * Onbekende sleutels, ongeldige kleuren of patronen vallen weg, en een afwijking die gelijk is
 * aan de standaard ook: dan is er niets af te wijken, en zo blijft 'terug naar norm' gewoon
 * het weglaten van de sleutel.
 */
export function sanitizeTextureOverrides(input: unknown): TextureOverrides {
  const out: TextureOverrides = {};
  if (!input || typeof input !== 'object' || Array.isArray(input)) return out;
  for (const [key, raw] of Object.entries(input as Record<string, unknown>)) {
    const e = CATALOG_BY_KEY.get(key);
    if (!e || !raw || typeof raw !== 'object') continue;
    const r = raw as Record<string, unknown>;
    const fill = normalizeColor(r.fill);
    const mark = normalizeColor(r.mark);
    const pattern = typeof r.pattern === 'string' && PATTERN_IDS.has(r.pattern) ? (r.pattern as TexturePattern) : null;
    if (!fill || !mark || !pattern) continue;
    const t: Texture = { fill, pattern, mark };
    if (e.category === 'bzb' && r.warn === true) t.warn = true;
    if (!sameTexture(t, e.defaultTexture)) out[key] = t;
  }
  return out;
}

/**
 * De textuur voor een code: de afwijking van het bedrijf, anders de standaard. Een code die
 * niet in de catalogus staat (oude of vrije waarde) krijgt de standaard van zijn familie op
 * basis van het label; zonder label 'bijzonder' (soil) of 'overig' (bzb).
 */
export function resolveTexture(
  standard: SoilStandard,
  category: TextureCategory,
  code: string | null | undefined,
  overrides?: TextureOverrides | null,
  label?: string | null,
): Texture {
  if (code) {
    const e = CATALOG_BY_CODE.get(textureKey(standard, category, code));
    if (e) return overrides?.[e.key] ?? e.defaultTexture;
    if (category === 'soil' && standard === 'nen5104' && HISTORIC_SOIL_5104.has(code)) {
      return NEN_FIGURE_1[soilFamilyOfLabel(HISTORIC_SOIL_5104.get(code)!)];
    }
  }
  if (category === 'bzb') return bzbDefaultTexture(label ? classifyBzbLabel(label) : null);
  if (label) return defaultForFamily(soilFamilyOfLabel(label));
  return NEN_FIGURE_1.bijzonder;
}

/**
 * De textuur van een vulmateriaal (`afwerking`, VUL_*-code) of een peilbuisonderdeel (`peilbuis`,
 * zie PEILBUIS_PARTS). Geldt voor beide normen. Een onbekende code krijgt het grijs van
 * uitkomende grond, zoals de tekening altijd deed.
 */
export function resolveInstallTexture(
  category: InstallCategory,
  code: string | null | undefined,
  overrides?: TextureOverrides | null,
  fallback: Texture = UNKNOWN_FILLING,
): Texture {
  const e = code ? CATALOG_BY_KEY.get(textureKey(null, category, code)) : undefined;
  if (e) return overrides?.[e.key] ?? e.defaultTexture;
  return fallback;
}

/**
 * Hoe diep de band van de bovenafwerking in het boorgat getekend wordt (cm onder maaiveld). Een
 * bovenafwerking heeft in de data alleen een hoogte t.o.v. maaiveld en geen diepte; dit is
 * ongeveer de diepte van een straatpot.
 */
export const TOP_COMPLETION_DEPTH_CM = 30;

/**
 * De textuur van een bovenafwerking (BOV_*-code), of null als er niets te tekenen is: zonder
 * eigen kleur of patroon (standaard) blijft de bovenafwerking uit de tekening.
 */
export function resolveTopCompletionTexture(code: string | null | undefined, overrides?: TextureOverrides | null): Texture | null {
  const t = resolveInstallTexture('afwerking', code, overrides, BZB_DEFAULT);
  return isBlankTexture(t) ? null : t;
}

/** De textuur van iets dat achtergelaten is in de grond, per type (CAS, DIV, ...). */
export function resolveLeftBehindTexture(type: string | null | undefined, overrides?: TextureOverrides | null): Texture {
  return resolveInstallTexture('afwerking', type, overrides, LEFT_BEHIND_DEFAULT);
}

/** Wijkt de textuur van dit vulmateriaal of peilbuisonderdeel af van de standaard? */
export function isInstallTextureCustom(category: InstallCategory, code: string, overrides?: TextureOverrides | null): boolean {
  return isTextureOverridden(textureKey(null, category, code), overrides);
}

/**
 * De bijzondere bestanddelen van een laag waarvoor het bedrijf een waarschuwing heeft aangezet
 * (kolom Waarschuwing in de tab BZBs). Leeg = geen waarschuwingsdriehoek.
 */
export function bzbWarnings(
  standard: SoilStandard,
  bzb: ReadonlyArray<{ code: string | null; label?: string | null }>,
  overrides?: TextureOverrides | null,
): string[] {
  if (!overrides) return [];
  return bzb.filter((b) => resolveTexture(standard, 'bzb', b.code, overrides, b.label ?? null).warn === true)
    .map((b) => b.label || b.code || 'BZB');
}

/** Wijkt deze textuur af van de standaard? (voor het label 'afwijkend van NEN 6693'). */
export function isTextureOverridden(key: string, overrides?: TextureOverrides | null): boolean {
  const e = CATALOG_BY_KEY.get(key);
  const own = overrides?.[key];
  return !!(e && own && !sameTexture(own, e.defaultTexture));
}

// ─── SVG-markup (backoffice, PDF) ───────────────────────────────────────────

/** `<pattern>`-element als SVG-tekst. De veldapp bouwt dezelfde vormen met react-native-svg. */
export function texturePatternSvg(id: string, texture: Texture): string {
  const p = getPatternDef(texture.pattern);
  const transform = p.rotate ? ` patternTransform="rotate(${p.rotate})"` : '';
  const shapes = p.shapes.map((s) => {
    if (s.type === 'circle') return `<circle cx="${s.cx}" cy="${s.cy}" r="${s.r}" fill="${texture.mark}"/>`;
    if (s.type === 'line') return `<line x1="${s.x1}" y1="${s.y1}" x2="${s.x2}" y2="${s.y2}" stroke="${texture.mark}" stroke-width="${s.strokeWidth}"/>`;
    return `<path d="${s.d}" stroke="${texture.mark}" stroke-width="${s.strokeWidth}" fill="${s.filled ? texture.mark : 'none'}"/>`;
  }).join('');
  return `<pattern id="${id}" width="${p.width}" height="${p.height}" patternUnits="userSpaceOnUse"${transform}>`
    + `<rect width="${p.width}" height="${p.height}" fill="${texture.fill}"/>${shapes}</pattern>`;
}
